// Components==============
import destroyTrainingAttachment from 'actions/training/attachment/destroy';
import storeTrainingAttachment from 'actions/training/attachment/store';
import updateTrainingAttachment from 'actions/training/attachment/update';
import destroyTrainingCategory from 'actions/training/category/destroy';
import storeTrainingCategory from 'actions/training/category/store';
import destroyTrainingSessionLessonContent from 'actions/training/session-lesson-content/destroy';
import storeTrainingSessionLessonContent from 'actions/training/session-lesson-content/store';
import updateTrainingSessionLessonContent from 'actions/training/session-lesson-content/update';
import destroyTrainingSessionLesson from 'actions/training/session-lesson/destroy';
import storeTrainingSessionLesson from 'actions/training/session-lesson/store';
import updateTrainingSessionLesson from 'actions/training/session-lesson/update';
import destroyTrainingSession from 'actions/training/session/destroy';
import indexSessions from 'actions/training/session/index';
import storeTrainingSession from 'actions/training/session/store';
import updateTrainingSession from 'actions/training/session/update';
import destroyTrainingSkill from 'actions/training/skill/destroy';
import storeTrainingSkill from 'actions/training/skill/store';
import indexTrainings from 'actions/training/training/index';
import storeTraining from 'actions/training/training/store';
import updateTraining from 'actions/training/training/update';
import { useAlert } from 'hooks/useAlert';
import { useConfirm } from 'hooks/useConfirm';
import { useReduxAction } from 'hooks/useReduxAction';
import { useTranslate } from 'hooks/useTranslate';
import { DropResult } from 'react-beautiful-dnd';
import { useHistory, useParams } from 'react-router';
import {
  LessonContentType,
  LessonType,
  SessionType,
  TrainingBuilderContextType,
} from './TrainingBuilder';
// =========================

export type TrainingBuilderFunctions = ReturnType<
  typeof useTrainingBuilderFunctions
>;

export function useTrainingBuilderFunctions({
  training,
  formValues,
  setImages,
  attachments,
  setAttachments,
  sessions,
  trainingSessions,
  setSessions,
  currentSession,
  setCurrentSession,
  setEditorView,
  setSaveCounter,
}: TrainingBuilderContextType) {
  const dispatch = useReduxAction();
  const history = useHistory();
  const alert = useAlert();
  const translate = useTranslate();
  const confirm = useConfirm();
  const params = useParams() as { trainingId?: string };

  function handleAttachmentUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];

    const imageReader = new FileReader();

    const id = Math.floor(Math.random() * 10e6);

    imageReader.addEventListener('load', () => {
      if (typeof imageReader.result === 'string') {
        setImages((prev) => [
          ...prev,
          {
            name: file?.name || '',
            path: imageReader.result as string,
            evidenceId: id,
          },
        ]);

        setAttachments((prev) => [
          ...prev,
          {
            id,
            data: { title: file?.name || '', text: '' },
            order_index: attachments.length,
            attachment_type: 'image',
            file,
            changed: true,
          },
        ]);
      }
    });

    if (file) imageReader.readAsDataURL(file);
  }

  function handleRemoveAttachment(id: number) {
    confirm(translate('course.confirm.delete'), () => {
      setImages((prev) => prev.filter((e) => e.evidenceId !== id));
      setAttachments((prev) => prev.filter((a) => a.id !== id));
    });
  }

  function handleAddExtraInfo() {
    setAttachments((prev) => [
      ...prev,
      {
        id: Math.floor(Math.random() * 10e6),
        data: { title: '', text: '' },
        order_index: attachments.length,
        attachment_type: 'text',
        changed: true,
      },
    ]);
  }

  function handleReorderExtraInfo({ source, destination }: DropResult) {
    if (!destination) return;

    const textAttachments = attachments
      .filter((a) => a.attachment_type === 'text')
      .sort((a, b) => a.order_index - b.order_index);

    const target = textAttachments[source.index];

    textAttachments.splice(source.index, 1);
    textAttachments.splice(destination.index, 0, target);

    const newTextAttachments = textAttachments.map((a, i) => ({
      ...a,
      order_index: i + 1,
      changed: true,
    }));

    setAttachments((prev) => {
      prev.filter((a) => a.attachment_type !== 'text');

      return [
        ...prev.filter((a) => a.attachment_type !== 'text'),
        ...newTextAttachments,
      ];
    });
  }

  function handleAddSession(title: string) {
    const newSession = {
      id: Math.floor(Math.random() * 10e6),
      title,
      image: undefined,
      imageFile: undefined,
      description: '',
      lessons: [],
      order_index: sessions.length,
      changed: true,
    } as SessionType;

    setSessions((prev) => [...prev, newSession]);
    setEditorView('session');
    setCurrentSession(newSession.id);
  }

  function handleDeleteSession() {
    confirm(translate('course.confirm.delete-session'), () => {
      setSessions((prev) =>
        prev.filter((session) => session.id !== currentSession)
      );
      if (sessions.length > 1) return setCurrentSession(sessions?.[0].id);

      setEditorView('training');
    });
  }

  function handleAddSessionImage(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];

    const imageReader = new FileReader();

    imageReader.addEventListener('load', () => {
      if (typeof imageReader.result === 'string') {
        setSessions((prev) =>
          prev.map((session) =>
            session?.id === currentSession
              ? ({
                  ...session,
                  imageFile: file,
                  image: imageReader.result,
                  changed: true,
                } as SessionType)
              : session
          )
        );
      }
    });

    if (file) imageReader.readAsDataURL(file);
  }

  function handleDeleteSessionImage() {
    setSessions((prev) =>
      prev.map((session) =>
        session?.id === currentSession
          ? ({
              ...session,
              imageFile: undefined,
              image: undefined,
              changed: true,
            } as SessionType)
          : session
      )
    );
  }

  function handleReorderSession({ source, destination }: DropResult) {
    if (!destination) return;

    const target = sessions[source.index];

    sessions.splice(source.index, 1);
    sessions.splice(destination.index, 0, target);

    const newSessions = sessions.map((c, i) => ({
      ...c,
      order_index: i + 1,
      changed: true,
    }));

    setSessions(newSessions);
  }

  function handleAddLesson(title: string) {
    const newLesson = {
      id: Math.floor(Math.random() * 10e6),
      title,
      description: '',
      order_index: sessions.find((s) => s.id === currentSession)?.lessons
        ?.length,
      evidence_required: 0,
      contents: [],
      changed: true,
    } as LessonType;

    setSessions((prev) =>
      prev.map((session) =>
        session.id === currentSession
          ? {
              ...session,
              lessons: session.lessons
                ? [...session?.lessons, newLesson]
                : [newLesson],
            }
          : session
      )
    );
    setEditorView('session');
  }

  function handleDeleteLesson(lessonId: number) {
    confirm(translate('course.confirm.delete-lesson'), () =>
      setSessions((prev) =>
        prev.map((session) =>
          session?.id === currentSession
            ? {
                ...session,
                lessons: session.lessons?.filter((l) => l.id !== lessonId),
              }
            : session
        )
      )
    );
  }

  function handleToggleEvidence(lessonId: number) {
    setSessions((prev) =>
      prev.map((s) =>
        s.id === currentSession
          ? {
              ...s,
              lessons: s.lessons?.map((l) =>
                l.id === lessonId
                  ? {
                      ...l,
                      evidence_required: l.evidence_required ? 0 : 1,
                      changed: true,
                    }
                  : l
              ),
            }
          : s
      )
    );
  }

  function handleReorderLesson({ source, destination }: DropResult) {
    if (!destination) return;

    const lessons =
      sessions.find((s) => s.id === currentSession)?.lessons || [];

    const target = lessons[source.index];

    lessons.splice(source.index, 1);
    lessons.splice(destination.index, 0, target);

    const newLessons = lessons.map((l, i) => ({
      ...l,
      order_index: i + 1,
      changed: true,
    }));

    setSessions((prev) =>
      prev.map((s) =>
        s.id === currentSession ? { ...s, lessons: newLessons } : s
      )
    );
  }

  function handleAddContentToLesson({
    type,
    lessonId,
  }: {
    type: 'image' | 'file' | 'video' | 'text';
    lessonId: number;
  }) {
    const newItem: LessonContentType = {
      id: Math.floor(Math.random() * 10e6),
      order_index:
        sessions
          .find((s) => s.id === currentSession)
          ?.lessons?.find((l) => l.id === lessonId)?.contents?.length || 0,
      block_type: type,
      block_data: JSON.stringify({ value: '' }),
      block_local_data: undefined,
      changed: true,
    };

    setSessions((prev) =>
      prev.map((s) =>
        s.id === currentSession
          ? {
              ...s,
              lessons: s.lessons?.map((l) =>
                l.id === lessonId
                  ? {
                      ...l,
                      contents: l.contents
                        ? [...l.contents, newItem]
                        : [newItem],
                    }
                  : l
              ),
            }
          : s
      )
    );
  }

  function handleDeleteLessonContent({
    lessonId,
    contentId,
  }: {
    lessonId: number;
    contentId: number;
  }) {
    setSessions((prev) =>
      prev.map((session) =>
        session?.id === currentSession
          ? {
              ...session,
              lessons: session.lessons?.map((l) =>
                l.id === lessonId
                  ? {
                      ...l,
                      contents: l.contents?.filter((c) => c.id !== contentId),
                      changed: true,
                    }
                  : l
              ),
            }
          : session
      )
    );
  }

  function handleLessonContentFileUpload({
    e,
    lessonId,
    contentId,
  }: {
    e: React.ChangeEvent<HTMLInputElement>;
    lessonId: number;
    contentId: number;
  }) {
    const file = e.target.files?.[0];

    const imageReader = new FileReader();

    imageReader.addEventListener('load', () => {
      if (typeof imageReader.result === 'string') {
        setSessions((prev) =>
          prev.map((session) =>
            session?.id === currentSession
              ? {
                  ...session,
                  lessons: session.lessons?.map((l) =>
                    l.id === lessonId
                      ? {
                          ...l,
                          contents: l.contents?.map((c) =>
                            c.id === contentId
                              ? {
                                  ...c,
                                  block_local_data: JSON.stringify({
                                    file_path: imageReader.result,
                                    file_name: file?.name,
                                  }),
                                  block_data: { file },
                                  changed: true,
                                }
                              : c
                          ),
                        }
                      : l
                  ),
                }
              : session
          )
        );
      }
    });

    if (file) imageReader.readAsDataURL(file);
  }

  function handleReorderLessonContent({
    source,
    destination,
    lessonId,
  }: DropResult & { lessonId: number }) {
    if (!destination) return;

    const content =
      sessions
        .find((s) => s.id === currentSession)
        ?.lessons?.find((l) => l.id === lessonId)?.contents || [];

    const target = content[source.index];

    content.splice(source.index, 1);
    content.splice(destination.index, 0, target);

    const newContent = content.map((c, i) => ({
      ...c,
      order_index: i + 1,
      changed: true,
    }));

    setSessions((prev) =>
      prev.map((s) =>
        s.id === currentSession
          ? {
              ...s,
              lessons: s.lessons?.map((l) =>
                l.id === lessonId ? { ...l, contents: newContent } : l
              ),
            }
          : s
      )
    );
  }

  /**
   * SAVING METHODS
   */

  function handleSave(andLook?: boolean) {
    const saveMethod = training ? updateTraining : storeTraining;

    let trainingId: number;
    let newFirstSessionId: number | undefined;

    function getAttachmentPromises() {
      const prevAttachments = training?.attachments || [];

      const attachmentIds = attachments?.map((a) => a.id);
      const prevAttachmentIds = prevAttachments.map((a) => a.id);

      const deletePromises = prevAttachmentIds
        .filter((attachmentId) => !attachmentIds?.includes(attachmentId))
        .map((attachmentId) =>
          dispatch(destroyTrainingAttachment({ attachmentId, trainingId }))
        );

      const createPromises = attachments
        ?.filter((a) => !prevAttachmentIds?.includes(a.id))
        .map(({ order_index, attachment_type, file, data }) =>
          dispatch(
            storeTrainingAttachment({
              trainingId,
              order_index,
              file,
              attachment_type,
              attachment_data: JSON.stringify(data),
            })
          )
        );

      const updatePromises = attachments
        .filter((a) => prevAttachmentIds?.includes(a.id))
        .filter((a) => a.changed)
        .map(({ id, data, file, order_index }) =>
          dispatch(
            updateTrainingAttachment({
              trainingId,
              file,
              attachmentId: id,
              order_index,
              attachment_data: JSON.stringify(data),
            })
          )
        );

      return [...deletePromises, ...createPromises, ...updatePromises];
    }

    function getSessionLessonContentPromises(
      internalSessionId: number,
      sessionId: number,
      internalLessonId: number,
      lessonId: number
    ) {
      const prevSessions = trainingSessions;

      const contents = sessions
        ?.find((s) => s?.id === internalSessionId)
        ?.lessons?.find((l) => l.id === internalLessonId)?.contents;

      let prevContents: TrainingLessonContent[] | undefined = [];

      try {
        prevContents = prevSessions
          ?.find((s) => s?.id === internalSessionId)
          ?.lessons?.find((l) => l?.id === internalLessonId)?.contents;
      } catch (e) {
        console.log(e);
      }

      const contentIds = contents?.map((c) => c.id);
      const prevContentIds = prevContents?.map((c) => c.id);

      const deletePromises =
        prevContentIds
          ?.filter((contentId) => !contentIds?.includes(contentId))
          .map((contentId) =>
            dispatch(
              destroyTrainingSessionLessonContent({
                contentId,
                lessonId,
                sessionId,
                trainingId,
              })
            )
          ) || [];

      const prepareFormData = ({
        id,
        block_type,
        block_data,
        order_index,
      }: LessonContentType) => {
        const formData = {
          id,
          block_type,
          block_data:
            typeof block_data === 'string'
              ? block_data
              : JSON.stringify(block_data),
          order_index,
          file:
            // @ts-ignore
            ['image', 'file'].includes(block_type) && block_data.file
              ? // @ts-ignore
                block_data.file
                ? // @ts-ignore
                  block_data.file
                : undefined
              : undefined,
        };
        return formData;
      };

      const createPromises =
        contents
          ?.filter((c) => !prevContentIds?.includes(c.id))
          .map(prepareFormData)
          .map((formData) =>
            dispatch(
              storeTrainingSessionLessonContent({
                trainingId,
                sessionId,
                lessonId,
                ...formData,
              })
            )
          ) || [];

      const updatePromises =
        contents
          ?.filter((c) => prevContentIds?.includes(c.id))
          .filter((a) => a.changed)
          .map(prepareFormData)
          .map((formData) =>
            dispatch(
              updateTrainingSessionLessonContent({
                trainingId,
                sessionId,
                lessonId,
                contentId: formData.id,
                ...formData,
              })
            )
          ) || [];

      return [...deletePromises, ...createPromises, ...updatePromises];
    }

    function getSessionLessonPromises(
      internalSessionId: number,
      sessionId: number
    ) {
      const prevSessions = training?.sessions || [];

      const lessons = sessions.find((s) => s.id === internalSessionId)?.lessons;
      const prevLessons =
        (prevSessions.find((s) => s.id === internalSessionId) || {})?.lessons ||
        [];

      const lessonIds = lessons?.map((l) => l.id);
      const prevLessonIds = prevLessons.map((l) => l.id);

      const deletePromises = prevLessonIds
        .filter((lessonId) => !lessonIds?.includes(lessonId))
        .map((lessonId) =>
          dispatch(
            destroyTrainingSessionLesson({
              lessonId,
              sessionId,
              trainingId,
            })
          )
        );

      const createPromises =
        lessons
          ?.filter((l) => !prevLessonIds.includes(l.id))
          .map(({ id, title, description, order_index, evidence_required }) =>
            dispatch(
              storeTrainingSessionLesson({
                trainingId,
                sessionId,
                title: title || undefined,
                description: description || undefined,
                evidence_required: evidence_required ? 1 : 0,
                order_index,
                is_published: training?.is_published ? 1 : 0,
              })
            ).then((lesson: LessonTrainingSessionEntity) =>
              Promise.all(
                getSessionLessonContentPromises(
                  internalSessionId,
                  sessionId,
                  id,
                  lesson.id
                )
              )
            )
          ) || [];

      const updatePromises =
        lessons
          ?.filter((l) => prevLessonIds.includes(l.id))
          .map(
            ({
              id,
              title,
              description,
              order_index,
              evidence_required,
              changed,
            }) => {
              const promise = changed
                ? dispatch(
                    updateTrainingSessionLesson({
                      trainingId,
                      sessionId,
                      lessonId: id,
                      order_index,
                      title: title || undefined,
                      description: description || undefined,
                      evidence_required: evidence_required ? 1 : 0,
                    })
                  )
                : Promise.resolve({
                    id,
                  });

              return promise.then((lesson: any) =>
                Promise.all(
                  getSessionLessonContentPromises(
                    internalSessionId,
                    sessionId,
                    id,
                    lesson.id
                  )
                )
              );
            }
          ) || [];
      return [...deletePromises, ...createPromises, ...updatePromises];
    }

    function getSessionPromises() {
      const prevSessions = training?.sessions || [];

      const sessionIds = sessions.map((s) => s.id);
      const prevSessionIds = prevSessions.map((s) => s.id);

      const deletePromises = prevSessionIds
        .filter((sessionId) => !sessionIds.includes(sessionId))
        .map((sessionId) =>
          dispatch(destroyTrainingSession({ sessionId, trainingId }))
        );

      const createPromises = sessions
        .filter((s) => !prevSessionIds.includes(s.id))
        .map(({ id, title, description, imageFile, order_index }) =>
          dispatch(
            storeTrainingSession({
              trainingId,
              title: title || undefined,
              description: description || undefined,
              image: imageFile || undefined,
              order_index,
              is_published: training?.is_published ? 1 : 0,
            })
          ).then((session: TrainingSessionEntity) => {
            if (!newFirstSessionId) newFirstSessionId = session?.id;
            return Promise.all(getSessionLessonPromises(id, session.id));
          })
        );

      const updatePromises = sessions
        .filter((s) => prevSessionIds.includes(s.id))
        .map(
          ({
            id,
            title,
            description,
            image,
            imageFile,
            order_index,
            changed,
          }) => {
            const promise = changed
              ? dispatch(
                  updateTrainingSession({
                    trainingId,
                    sessionId: id,
                    order_index,
                    image: imageFile,
                    delete_image: image !== undefined ? 0 : 1,
                    title: title || undefined,
                    description: description || undefined,
                  })
                )
              : Promise.resolve({ id });

            return promise.then((session: any) =>
              Promise.all(getSessionLessonPromises(id, session.id))
            );
          }
        );

      return [...deletePromises, ...createPromises, ...updatePromises];
    }

    function getSkillPromises() {
      const prevSkills = training?.skills?.map((s) => s.skill.id);
      const { skills } = formValues;

      const createPromises =
        skills
          .filter((skillId) => !prevSkills?.includes(skillId))
          .map((skillId) =>
            dispatch(storeTrainingSkill({ skillId, trainingId }))
          ) || [];

      const deletePromises =
        prevSkills
          ?.filter((skillId) => !skills.includes(skillId))
          .map((skillId) =>
            dispatch(
              destroyTrainingSkill({
                skillId: training?.skills?.find((s) => s.skill_id === skillId)
                  ?.id,
                trainingId,
              })
            )
          ) || [];

      return [...createPromises, ...deletePromises];
    }

    function getCategoryPromises() {
      const prevCat = training?.categories?.map((c) => c.category_id);
      const { categories } = formValues;

      const createPromises =
        categories
          .filter((id) => !prevCat?.includes(id))
          .map((id) =>
            dispatch(storeTrainingCategory({ categoryId: id, trainingId }))
          ) || [];

      const deletePromises =
        prevCat
          ?.filter((id) => !categories.includes(id))
          .map((id) =>
            dispatch(
              destroyTrainingCategory({
                categoryId: training?.categories?.find(
                  (c) => c.category_id === id
                )?.id,
                trainingId,
              })
            )
          ) || [];

      return [...createPromises, ...deletePromises];
    }

    dispatch(
      saveMethod({
        visibility: training?.visibility,
        title: formValues.title,
        description: formValues.description,
        youtube: formValues.youtube || undefined,
        trainingId: params.trainingId,
      })
    )
      .then((res: TrainingEntity) => {
        trainingId = res.id;

        return Promise.all([
          ...getAttachmentPromises(),
          ...getCategoryPromises(),
          ...getSkillPromises(),
          ...getSessionPromises(), // lessons and contents are called within the session promises
        ]);
      })
      .then(() => {
        if (andLook) {
          if (sessions.length)
            return history.push(
              `/werkplaats/training-details/${trainingId}/${
                newFirstSessionId || sessions?.[0].id
              }`
            );
          return history.push(`/werkplaats/training-details/${trainingId}`);
        }
      })
      .then(() =>
        Promise.all([
          dispatch(indexTrainings(), dispatch(indexSessions({ trainingId }))),
        ])
      )
      .then(() => {
        if (!training)
          history.push(`/werkplaats/training-bouwer/${trainingId}`);

        if (!andLook) alert(translate('course.success.saving'), 'Yes!');
      })
      .then(() => {
        setTimeout(() => {
          setSaveCounter((prev) => prev + 1);
        }, 1000);
        if (newFirstSessionId) setCurrentSession(newFirstSessionId);
      })
      .catch(({ response }: { response: any }) => {
        alert(
          `<p>${translate('course.errors.saving')}
          </br> 
          <strong>${
            response?.data?.errors
              ? Object.values(response?.data?.errors)?.[0]
              : ''
          }</strong></p>`
        );
      });
  }

  return {
    handleSave,
    handleAttachmentUpload,
    handleRemoveAttachment,
    handleAddExtraInfo,
    handleReorderExtraInfo,
    handleAddSession,
    handleDeleteSession,
    handleAddSessionImage,
    handleDeleteSessionImage,
    handleAddLesson,
    handleDeleteLesson,
    handleToggleEvidence,
    handleAddContentToLesson,
    handleDeleteLessonContent,
    handleLessonContentFileUpload,
    handleReorderLessonContent,
    handleReorderSession,
    handleReorderLesson,
  };
}
